import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { EcwidLink } from '@/components/EcwidWidgets';
import { X, ArrowRight } from 'lucide-react';
import { getProduct } from '@/lib/api';
import { EASE } from '@/lib/motion';
import { Price } from '@/components/ProductCard';
import { track } from '@/lib/seo';

export default function QuickView({ slug, onClose }) {
  const [product, setProduct] = useState(null);

  useEffect(() => {
    getProduct(slug).then((p) => {
      setProduct(p);
      track('view_product', { item: p.name });
    }).catch(() => setProduct({ error: true }));
  }, [slug]);

  const buyUrl = product ? `/shop#!/~/product/id=${product.id}` : '/shop';

  return (
    <motion.div
      data-testid="quick-view-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="relative grid max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-ink/10 bg-bone md:grid-cols-2"
      >
        <button data-testid="quick-view-close-button" onClick={onClose} aria-label="Close quick view" className="absolute right-4 top-4 z-10 border border-ink/10 bg-bone p-2 text-sage transition-colors hover:text-ink">
          <X className="h-5 w-5" />
        </button>

        <div className="aspect-square bg-parchment">
          {product?.image && <img src={product.image} alt={product.name} className="h-full w-full object-cover" />}
        </div>

        <div className="flex flex-col p-8">
          {!product ? (
            <p className="text-sage">Loading…</p>
          ) : product.error ? (
            <p className="text-sage">Could not load this product. Please try again.</p>
          ) : (
            <>
              {product.categories?.[0] && (
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-forest">{product.categories[0].name}</span>
              )}
              <h3 className="mt-2 text-3xl font-extrabold tracking-tight text-ink">{product.name}</h3>
              <div className="mt-3 text-base font-bold text-bronze"><Price product={product} /></div>
              {product.shortDescription && (
                <p className="mt-4 text-sm leading-relaxed text-sage">{product.shortDescription}</p>
              )}
              {product.variations?.length > 0 && (
                <p className="mt-4 text-xs uppercase tracking-[0.15em] text-ash">
                  Options: {product.variations.map((v) => Object.values(v.attributes || {}).join(' / ')).join(' · ')}
                </p>
              )}

              <EcwidLink
                to={buyUrl}
                onClick={onClose}
                data-testid="quick-view-buy-button"
                className="mt-8 flex items-center justify-center gap-2 bg-forest py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-bone transition-colors hover:bg-moss"
              >
                Choose Options & Buy <ArrowRight className="h-4 w-4" />
              </EcwidLink>
              <p className="mt-4 text-center text-[11px] text-ash">Secure checkout by Ecwid</p>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
